/*
 * Authors: mariushirjanu
 * Created: 18/02/2019
 */
import * as _ from 'underscore';
import { EventEmitter, Injectable } from '@angular/core';
import { AuthService } from '../../shared/services/auth/auth.service';
import { IUserData } from '../../shared/services/auth/auth-data.service';
import { ICompany, ICompanyCountry, IContact, ICountryExtended, IRecord, IValueObject, IWebshop } from '../../../type_definitions/datatypes';
import { CompanyDataService, CompanyType, ICodeDescription } from '../../shared/company/company-data.service';
import { IWebshopInfo } from './info/webshop-info-data.service';
import { GaDeferredService, IGADeferred } from '../../shared/services/ga-deferred.service';
import { GaWizard } from '../../shared/wizard/ga-wizard';
import { GaWizardService } from '../../shared/wizard/ga-wizard.service';
import { WIZARD_WEBSHOP } from '../../shared/app-wizards';

export interface IWebshopData {
  id?:number;
  name:string;
  customer?:ICompany;
  limitToCustomer:boolean;
  webshopName:string;
  webshopDescription?:string;
  expectedDecisionDate?:Date;
  expectedStartDate?:Date;
  expectedOrdersPerYear?:number;
  revenueForecast?:number;
  catalogAvailable?:IValueObject;
  catalogUploadDate?:Date;
  catalogComment?:string;
  userListAvailable?:IValueObject;
  userListUploadDate?:Date;
  usersComment?:string;
  lamType:IValueObject;
  lamCompanyCountry?:ICompanyCountry;
  lamCompanyDepartment?:ICompanyCountry;
  lamCustomerCountry?:ICountryExtended;
  lamMainContacts:IUserData[];
  superLAM?:IContact;
  isMainOEM:boolean;
  oem?:ICompany;
  oemMainContacts?:IContact[];
  oemCustomerId:string;
  oemCustomerType?:ICodeDescription;
  gbdm?:IContact;
}

export interface IWebshopRequest {
  customerCompanyId?:number;
  name:string;
  description?:string;
  expectedDecisionDate?:Date;
  expectedStartDate:Date;
  expectedOrdersPerYear?:number;
  revenueForecastUSD:number;
  catalogAvailable:boolean;
  expectedCatalogUploadDate?:Date;
  catalogUploadComment:string;
  userListAvailable:boolean;
  expectedUserListUploadDate?:Date;
  userListUploadComment?:string;
  lamType?:string;
  lamId?:number;
  mainLamContacts:number[];
  customerTypeId?:number;
  mainOem?:boolean;
  oemId:number;
  vgams?:number[];
  customerIdWithOem?:string;
  gbdm?:number;
}

export enum PartnerRelationType {
  INVOICING = 'INVOICING',
  PROCUREMENT = 'PROCUREMNT',
  LESSOR = 'LESSOR',
  HELPDESK = 'HELPDESK',
  SYSTEM_INTEGRATION = 'SYSTEM_INT'
}

export interface IExternalPartnerRelation extends IRecord {
  relationType?: PartnerRelationType;
  externalPartner?: ICompany;
}

@Injectable()
export class WebshopService {
  public static NEW_WEBSHOP:string = 'NEW';
  public onSaved:EventEmitter<Number>;
  public isInDialog: boolean;

  private webshopId:number;
  private webshop:IWebshop;
  private webshopDeferred:IGADeferred<IWebshop>;
  private webshopProfileData:IWebshopData;
  private wizard:GaWizard;

  constructor(
    private authService:AuthService,
    private companyDataService:CompanyDataService,
    private deferredService:GaDeferredService,
    wizardService:GaWizardService
  ) {
    this.wizard = wizardService.getInstance(WIZARD_WEBSHOP);
    this.webshopDeferred = this.deferredService.defer<IWebshop>();
  }

  // set the promise for the selected webshop (webshopId specified in url parameter)
  public setWebshopId(webshopId?:string):void {
    if (webshopId === WebshopService.NEW_WEBSHOP || !webshopId) {
      this.setWebshopData(<any>{});
    } else if (webshopId) {
      this.webshopId = Number(webshopId);

      this.setWebshopData(<any>{
        id: this.webshopId
      });
    }
  }

  public getWebshopId():number {
    return this.webshopId;
  }

  public setWebshop(webshop:IWebshop):void {
    this.webshop = webshop;
    this.webshopDeferred.resolve(webshop);
  }

  public getWebshop():IWebshop {
    return this.webshop;
  }

  public getWebshopPromise():Promise<IWebshop> {
    return this.webshopDeferred.promise;
  }

  public setWebshopData(webshopProfile:IRecord):void {
    const currentUser:IUserData = this.authService.getCurrentUser();
    this.webshopId = webshopProfile && webshopProfile.id;
    const userCompany:ICompany = currentUser.location && currentUser.location.companyCountry && currentUser.location.companyCountry.company;
    const companyType:string = userCompany && userCompany.companyType;
    this.webshopProfileData = <any>{
      id: this.webshopId,
      limitToCustomer: true,
      lamType: !this.authService.checkAcslUser() && companyType? <any>{ id: companyType } : undefined,
      lamMainContacts: !this.authService.checkAcslUser()? [ currentUser ] : undefined,
      isMainOEM: true,
      oem: this.authService.checkVendorUser()? userCompany : undefined,
      oemMainContacts: this.authService.checkVendorUser()? [ currentUser ] : undefined
    };
  }

  public getWebshopProfile():IWebshopData {
    return this.webshopProfileData;
  }

  public createWebshopProfileRequest():Promise<IWebshopRequest> {
    return this.getLAM()
      .then((lam:ICompanyCountry) => {
        return {
          customerCompanyId: this.webshopProfileData.customer && this.webshopProfileData.customer.id,
          name: this.webshopProfileData.webshopName,
          description: this.webshopProfileData.webshopDescription,
          expectedDecisionDate: this.webshopProfileData.expectedDecisionDate,
          expectedStartDate: this.webshopProfileData.expectedStartDate,
          expectedOrdersPerYear: this.webshopProfileData.expectedOrdersPerYear,
          revenueForecastUSD: this.webshopProfileData.revenueForecast,
          catalogAvailable: this.webshopProfileData.catalogAvailable && <boolean>this.webshopProfileData.catalogAvailable.id,
          expectedCatalogUploadDate: this.webshopProfileData.catalogUploadDate,
          catalogUploadComment: this.webshopProfileData.catalogComment,
          userListAvailable: this.webshopProfileData.userListAvailable && <boolean>this.webshopProfileData.userListAvailable.id,
          expectedUserListUploadDate: this.webshopProfileData.userListUploadDate,
          userListUploadComment: this.webshopProfileData.usersComment,
          lamType: this.webshopProfileData.lamType && <string>this.webshopProfileData.lamType.id,
          lamId: lam && lam.id,
          mainLamContacts: _.map(this.webshopProfileData.lamMainContacts, 'id'),
          superLamId: this.webshopProfileData.superLAM && this.webshopProfileData.superLAM.id,
          customerTypeId: this.webshopProfileData.oemCustomerType && this.webshopProfileData.oemCustomerType.id,
          mainOem: this.webshopProfileData.isMainOEM,
          oemId: this.webshopProfileData.oem && this.webshopProfileData.oem.id,
          vgams: _.map(this.webshopProfileData.oemMainContacts, 'id'),
          customerIdWithOem: this.webshopProfileData.oemCustomerId,
          gbdm: this.webshopProfileData.gbdm && this.webshopProfileData.gbdm.id
        };
      });
  }

  public getLAM(data?:IWebshopInfo | IWebshopRequest, customerId?:number):Promise<ICompanyCountry> {
    const webshopData:any = data || this.webshopProfileData;
    const lamType:string = webshopData.lamType && <string>webshopData.lamType.id;
    switch (lamType) {
      case CompanyType.BP:
        return Promise.resolve(webshopData.lamCompanyCountry); // id of BP LAM company country
      case CompanyType.OEM:
        return Promise.resolve(webshopData.lamCompanyDepartment); // id of OEM LAM company department
      case CompanyType.CUSTOMER: {
        customerId = customerId || this.webshopProfileData?.customer?.id;
        const lamCustomerCountryId:number = webshopData.lamCustomerCountry && webshopData.lamCustomerCountry.id;

        if (customerId && lamCustomerCountryId) {
          return this.companyDataService.getCompanyCountries(customerId, lamCustomerCountryId)
            .then((companyCountries:ICompanyCountry[]) => companyCountries?.[ 0 ]); // id of the customer company country
        }
        return Promise.resolve(undefined);
      }
      default:
        return Promise.resolve(undefined);
    }
  }

  public clear():void {
    this.webshopId = undefined;
    this.webshop = undefined;
    this.webshopDeferred = this.deferredService.defer();
    this.webshopProfileData = undefined;
    this.onSaved = undefined;
    this.isInDialog = undefined;
  }

  public updateWizardButtonStatus(busy:boolean):void {
    this.wizard.actions().forEach(a => {
      this.wizard.setActionBusy(a.id, busy);
      this.wizard.setActionEnabled(a.id, !busy);
    });
  }
}